import React, { useMemo } from "react";
import { Table } from "../UI/Table";
import { format } from "date-fns";
import { BILLABLE_OPTIONS, PAY_CODE_OPTIONS } from "../../utils/constants";
import { StyledTimesheetViewMode } from "./styles";
import moment from "moment";
import { translate } from "../../utils/translations/translate";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { FETCH_FILE_DATA } from "../../utils/services/constants";
import { getAuthHeaders } from "../../utils/services/auth";
import { useFetch } from "../../utils/hooks/useFetch";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";

const MultiLineHeader = (props) => {
  const { displayName, secondaryName } = props;
  return (
    <div className="ag-custom-header-cell">
      <div>{displayName}</div>
      <div>{secondaryName}</div>
    </div>
  );
};

export const TimesheetViewMode = ({
  taskRows,
  selectedWeek,
  approverComments,
  managerEmail,
  managerName,
  projectId,
  projectName,
  status,
  isEditable,
  comments,
  onModeChange,
  projectSavedData,
}) => {
  const formattedWeekLabels = useMemo(() => {
    const uniqueDates = [
      ...new Set(
        taskRows.flatMap((task) => task.hours.map((h) => h.comparator))
      ),
    ];

    return uniqueDates.map((date) => ({
      original: date,
      formatted: format(new Date(date), "EEE, MM-dd-yy"),
    }));
  }, [taskRows]);
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Project Task",
        field: "projectTaskName",
        width: 120,
        sortable: false,
      },
      {
        headerName: "Billable Type",
        field: "billableType",
        width: 140,
        sortable: false,
      },
      {
        headerName: "Pay Code",
        field: "payCode",
        width: 140,
        sortable: false,
      },
      ...formattedWeekLabels.map(({ original }) => ({
        headerComponent: MultiLineHeader,
        headerComponentParams: {
          displayName: `${format(new Date(original), "EEE")}`,
          secondaryName: `${format(new Date(original), "MM-dd")}`,
        },
        field: original,
        resizable: true,
        width: 90,
        cellStyle: {
          textAlign: "left",
          marginLeft: "0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        wrapHeaderText: true,
        autoHeaderHeight: true,
        valueGetter: (params) => params.data[original] ?? "-",
      })),

      {
        headerComponent: MultiLineHeader,
        headerComponentParams: {
          displayName: "Total",
        },
        field: "total",
        width: 110,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        valueGetter: (params) =>
          formattedWeekLabels.reduce(
            (sum, { original }) => sum + (params.data[original] || 0),
            0
          ),
      },
    ],
    [formattedWeekLabels]
  );

  const rowData = useMemo(
    () =>
      taskRows.map((task) => {
        const row = {
          projectTaskName: task.projectTaskName,
          billableType:
            BILLABLE_OPTIONS.find(
              (option) => option.value === task.billableType
            )?.label ?? "N/A",
          payCode:
            PAY_CODE_OPTIONS.find((option) => option.value === task.payCode)
              .label ?? "N/A",
        };
        let total = 0;
        task.hours.forEach((h) => {
          row[h.comparator] = h.hours ?? 0;
          total += h.hours ?? 0;
        });
        row.total = total;
        return row;
      }),
    [taskRows]
  );
  const columnTotals = useMemo(() => {
    const totals = { projectTaskName: "Total", billableType: "", payCode: "" };
    formattedWeekLabels.forEach(({ original }) => {
      totals[original] = rowData.reduce(
        (sum, row) => sum + (row[original] || 0),
        0
      );
    });
    totals.total = rowData.reduce((sum, row) => sum + row.total, 0);
    return totals;
  }, [rowData, formattedWeekLabels]);

  const downloadFile = async (fileName) => {
    try {
      const response = await performAction(
        `${FETCH_FILE_DATA}?FileName=${fileName}`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        }
      );

      const blob = await response.blob();
      const finalFileName = fileName.split("/").pop();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = finalFileName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error fetching file:", error);
    }
  };

  const handleOpenFile = (file) => {
    debugger;
    const imageExtensions = [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "bmp",
      "webp",
      "tiff",
      "svg",
      "pdf",
      "csv",
      "txt",
    ];
    // get file extension
    const extension = file.split(".").pop().toLowerCase();
    if (imageExtensions.includes(extension.toLowerCase())) {
      window.open(file, "_blank");
    } else {
      const fileUrl = encodeURIComponent(file);
      window.open(
        `https://view.officeapps.live.com/op/view.aspx?src=${fileUrl}`,
        "_blank"
      );
    }
  };

  const { performAction } = useFetch();

  return (
    <StyledTimesheetViewMode>
      {isEditable && (
        <button
          className="tima_time_edit-button"
          title="Exit timesheet edit mode"
          onClick={() => {
            onModeChange(projectId, "edit");
          }}
        >
          <BorderColorIcon titleAccess="Enter timesheet edit mode" />
        </button>
      )}
      <section className="tma_proj_details">
        <div className="tma_prj_info_container">
          <p className="tma_pnf_label">{translate("labels.dateRange")}</p>
          <p className="tma_pnf_value">
            {moment(selectedWeek.firstDay).format("MM/DD/YYYY")} -{" "}
            {moment(selectedWeek.lastDay).format("MM/DD/YYYY")}
          </p>
        </div>
        <div className="tma_prj_info_container">
          <p className="tma_pnf_label">{translate("labels.projectName")}</p>
          <p className="tma_pnf_value">{projectName}</p>
        </div>
        <div className="tma_prj_info_container">
          <p className="tma_pnf_label">{translate("labels.projectId")}</p>
          <p className="tma_pnf_value">{projectId}</p>
        </div>
        <div className="tma_prj_info_container">
          <p className="tma_pnf_label">{translate("labels.manager")}</p>
          <p className="tma_pnf_value">
            {managerName} - {managerEmail}
          </p>
        </div>
        {status && (
          <div className="tma_prj_info_container">
            <p className="tma_pnf_label">{translate("labels.status")}</p>
            <p className="tma_pnf_value">{status}</p>
          </div>
        )}
      </section>
      <div className="tma_approver_section">
        {approverComments && (
          <div className="tma_prj_info_container">
            <p className="tma_pnf_label">
              {translate("labels.approverComments")}:
            </p>
            <p className="tma_pnf_value">{approverComments}</p>
          </div>
        )}
        {comments && (
          <div className="tma_prj_info_container">
            <p className="tma_pnf_label">
              {translate("labels.employeeComments")}:
            </p>
            <p className="tma_pnf_value">{comments}</p>
          </div>
        )}
      </div>
      <div className="tma_timesheet_table">
        <Table
          columns={columnDefs}
          rowData={[...rowData, columnTotals]}
          showPagination={false}
          defaultColDef={{ resizable: true }}
          rowSelection={"single"}
        />
      </div>
      {!Array.isArray(projectSavedData?.attachments) && (
        <section className="tma_proj_details">
          <div className="tma_prj_info_container">
            <p className="tma_pnf_label">{translate("labels.attachments")}</p>
            <div className="tma_pnf_value">
              {projectSavedData?.attachments === "" ? (
                <p>No attachments are available.</p>
              ) : (
                projectSavedData?.attachments?.split(",").map((item, index) => (
                  <div style={{ display: "flex" }}>
                    <div
                      className="file_download"
                      key={index}
                      style={{ marginBottom: "10px" }}
                      onClick={() => handleOpenFile(item)}
                    >
                      {/* <a
                        target="_blank"
                        href={item}
                        style={{ textDecoration: "none" }}
                      > */}
                      {index + 1}. {item?.split("Attachments_")[1]?.trim()}
                      {/* </a> */}
                    </div>{" "}
                    &nbsp;
                    {/* {item
                      .split("?")[0]
                      .split("#")[0]
                      .split(".")
                      .pop()
                      .toLowerCase() === "pdf" ? ( */}
                    <a onClick={() => downloadFile(item)}>
                      <DownloadIcon
                        style={{
                          fontSize: "20px",
                          color: "#1b73d8",
                          cursor: "pointer",
                        }}
                      />
                    </a>
                    {/* ) : null} */}
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      )}
    </StyledTimesheetViewMode>
  );
};
